import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@column/ui';

import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { RenderFields, RestrictedBanner } from '~/components';
import { StatusBadge } from '~/components/Table';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { EventType, EventTypes, Webhook, WebhookRepository, WebhookVerify } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';

import { EventTypeCombobox } from './_components/EventTypeCombobox';
import { WebhookResultModal } from './_components/WebhookResultModal';
import { isEventTypeDeprecated } from './deprecatedEvents';

interface Params {
  id: string;
  URLType: string;
}

type WebhookFormValues = Omit<Webhook, 'id' | 'createdAt' | 'updatedAt'> & {
  type: string;
  temporaryData?: {
    testEventType?: string;
    webhookResult?: WebhookVerify;
  };
};

const SecretInput: React.FC<{ value: string }> = ({ value }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Input
        variant="copy"
        readOnly
        showCopyButton
        type={isVisible ? 'text' : 'password'}
        value={value}
        placeholder="Secret will be generated"
        className="flex-1"
      />
      <Button
        type="button"
        variant="secondary"
        size="icon-lg"
        aria-label={isVisible ? 'Hide secret' : 'Show secret'}
        onClick={() => setIsVisible((visible) => !visible)}
      >
        {isVisible ? <Icon.EyeClosed /> : <Icon.Eye />}
      </Button>
    </div>
  );
};

export const PageWebhooksEdit: React.FC = () => {
  const { currentPermission, currentUser, currentPlatform } = useSessionStore();
  const canWrite = currentPermission?.webhooks === 'write';
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id, URLType } = useParams<keyof Params>() as Params;
  const isCreate = !id;
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [isWebhookResultModalOpen, setIsWebhookResultModalOpen] = useState<boolean>(false);
  const [modalTestEventType, setModalTestEventType] = useState<string>('');
  const [allEvents, setAllEvents] = useState<boolean>(true);
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'page' });
  const createdAtRef = useRef<string | undefined>(undefined);

  const eventTypes = useMemo(
    () => Object.values(EventTypes).filter((name: string) => !['UNRECOGNIZED'].includes(name)),
    []
  );

  const wildcards = useMemo(
    () => [
      ...new Set(
        eventTypes
          .map((name: string) => {
            const elements = name.split('.');
            const array: string[] = [];
            if (typeof elements[1] !== 'undefined') array.push(`${elements[0]}.*`);
            if (typeof elements[2] !== 'undefined') array.push(`${elements[0]}.${elements[1]}.*`);
            return array;
          })
          .flat()
      ),
    ],
    [eventTypes]
  );

  const formMethods = useForm<WebhookFormValues>({
    defaultValues: {
      url: '',
      description: '',
      enabledEvents: ['*'],
      isDisabled: false,
      secret: '',
      type: 'sandbox',
      temporaryData: {},
    },
  });
  const { handleSubmit, setValue, reset, getValues, watch } = formMethods;
  const temporaryData = watch('temporaryData') ?? {};
  useBreadcrumbTitle(id ? webhookUrl : null);

  const watchedEnabledEvents = watch('enabledEvents');
  const enabledEvents = useMemo(() => watchedEnabledEvents ?? [], [watchedEnabledEvents]);
  const eventsAnchorRef = useRef<HTMLDivElement | null>(null);

  const testableEvents = useMemo(
    () =>
      allEvents
        ? eventTypes
        : [
            ...new Set(
              enabledEvents
                .filter((eventId: string) => eventId !== '*')
                .flatMap((eventId: string) => {
                  if (eventId.includes('*')) {
                    return eventTypes.filter((name: string) => name.startsWith(eventId.replace('*', '')));
                  }
                  return eventId;
                })
            ),
          ],
    [allEvents, eventTypes, enabledEvents]
  );

  const availableEvents = useMemo(
    () =>
      [...eventTypes, ...wildcards]
        // Hide events already covered by a selected top-level wildcard, but keep selected ones listed so they can be
        // unchecked.
        .filter(
          (eventId: string) => enabledEvents.includes(eventId) || !enabledEvents.includes(`${eventId.split('.')[0]}.*`)
        )
        // Keep deprecated event types selectable, but de-emphasize them by sorting them to the
        // bottom so they aren't picked for new endpoints by default.
        .sort((a: string, b: string) => {
          const aDeprecated = isEventTypeDeprecated(a, eventTypes);
          const bDeprecated = isEventTypeDeprecated(b, eventTypes);
          if (aDeprecated !== bDeprecated) {
            return aDeprecated ? 1 : -1;
          }
          return a.localeCompare(b);
        }),
    [eventTypes, wildcards, enabledEvents]
  );

  const fetchData = React.useCallback(() => {
    WebhookRepository.get(id, URLType === 'Sandbox')
      .then((response) => {
        setWebhookUrl(response.url ?? '');
        reset({
          url: response.url ?? '',
          description: response.description ?? '',
          enabledEvents: response.enabledEvents ?? ['*'],
          isDisabled: response.isDisabled ?? false,
          secret: response.secret ?? '',
          type: URLType?.toLowerCase() ?? 'sandbox',
        });
        setAllEvents(!!response.enabledEvents?.includes('*'));
        createdAtRef.current = response.createdAt;
      })
      .catch((error: unknown) => {
        navigate(ROUTE.PLATFORM_WEBHOOKS);
        addDangerNotification({ content: (error as Error).message });
      });
  }, [id, URLType, reset, navigate, addDangerNotification]);

  useEffect(() => {
    if (id && currentUser) fetchData();
  }, [id, currentUser, fetchData]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => {
          if (id && currentUser) fetchData();
        }
      ),
    [id, currentUser, fetchData]
  );

  const handleModalOpen = (eventType: string, webhookResultProps?: WebhookVerify) => {
    setModalTestEventType(eventType);
    if (webhookResultProps) {
      setValue('temporaryData', { ...temporaryData, webhookResult: webhookResultProps });
    }
    setIsWebhookResultModalOpen(true);
  };

  const handleSave = React.useCallback(
    (data: WebhookFormValues, callback?: () => void) => {
      if (!id || !currentUser) return;
      WebhookRepository.update(
        id,
        {
          description: typeof data.description === 'string' ? data.description : '',
          enabledEvents: allEvents
            ? ['*']
            : Array.isArray(data.enabledEvents)
              ? data.enabledEvents.filter((eventId) => eventId !== '*')
              : [],
          isDisabled: !!data.isDisabled,
          url: typeof data.url === 'string' ? data.url : '',
        },
        URLType === 'Sandbox'
      )
        .then(() => {
          if (callback) {
            callback();
            return;
          }
          addSuccessNotification({ content: 'Webhook saved' });
        })
        .catch((error: ApiError) => {
          if (error?.message) {
            addDangerNotification({
              content: error.message,
              actionButton: error.documentationUrl
                ? {
                    label: 'Docs',
                    onClick: () => window.open(error.documentationUrl, '_blank'),
                  }
                : undefined,
            });
          } else if (error instanceof Error) {
            addDangerNotification({ content: error.message });
          } else {
            addDangerNotification({ content: 'Unknown error' });
          }
        });
    },
    [id, currentUser, allEvents, URLType, addSuccessNotification, addDangerNotification]
  );

  const onSubmit = (data: WebhookFormValues) => {
    if (!currentUser) return;
    if (id) {
      handleSave(data);
      return;
    }

    WebhookRepository.create(
      {
        description: data.description ?? '',
        enabledEvents: allEvents ? ['*'] : (data.enabledEvents ?? []),
        url: data.url ?? '',
      },
      data.type === 'sandbox'
    )
      .then((response) => {
        navigate(
          `${ROUTE.PLATFORM_WEBHOOKS}/edit/${data.type === 'sandbox' ? 'Sandbox' : 'Production'}/${response.id}`
        );
        addSuccessNotification({ content: 'Webhook added' });
      })
      .catch((error: unknown) => {
        addDangerNotification({ content: (error as Error).message });
      });
  };

  const handleTestEventTypeChange = (eventType: string) => {
    setValue('temporaryData', { ...temporaryData, testEventType: eventType });
  };

  const handleTestWebhook = () => {
    if (!currentUser) return;
    if (!temporaryData.testEventType) {
      addDangerNotification({ content: 'Select an event type first' });
      return;
    }
    handleSave(getValues(), () => {
      WebhookRepository.verify(id, { eventType: temporaryData.testEventType as EventType }, URLType === 'Sandbox')
        .then((response) => {
          setValue('temporaryData', { ...temporaryData, webhookResult: response });
          if (response.success) {
            addSuccessNotification({
              content: `${temporaryData.testEventType} successfully sent`,
              actionButton: {
                label: 'View Result',
                onClick: () => handleModalOpen(temporaryData.testEventType!, response),
              },
            });
            return;
          }
          addDangerNotification({
            content: `${response.responseCode} error`,
            actionButton: {
              label: 'View Result',
              onClick: () => handleModalOpen(temporaryData.testEventType!, response),
            },
          });
        })
        .catch((error: unknown) => {
          addDangerNotification({ content: (error as Error).message });
        });
    });
  };

  const submitForm = handleSubmit(onSubmit, handleSubmitErrors);

  const submitButton = (
    <Button type="button" size="sm" disabled={!canWrite} onClick={submitForm}>
      {!id && <Icon.Plus />}
      {id ? 'Save Changes' : 'Create Webhook'}
    </Button>
  );

  return (
    <>
      <WebhookResultModal
        isOpen={isWebhookResultModalOpen}
        onClose={() => setIsWebhookResultModalOpen(false)}
        webhookId={id}
        isSandbox={URLType === 'Sandbox'}
        webhookResult={temporaryData.webhookResult}
        testableEvents={testableEvents}
        testEventType={modalTestEventType}
      />

      <PageActions>{submitButton}</PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}
      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={submitForm}>
          {RenderFields<WebhookFormValues>({
            sections: [
              {
                headline: 'General',
                fields: [
                  {
                    id: 'url',
                    label: 'URL',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                      <Input
                        value={String(value)}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="URL"
                        aria-invalid={isTouched && !!error}
                      />
                    ),
                  },
                  {
                    id: 'temporaryData.testEventType',
                    label: 'Test',
                    hide: isCreate,
                    children: () => (
                      <div className="flex items-center gap-2">
                        <EventTypeCombobox
                          className="flex-1"
                          value={temporaryData.testEventType ?? ''}
                          onValueChange={handleTestEventTypeChange}
                          eventTypes={testableEvents}
                        />
                        <Button type="button" variant="secondary" onClick={handleTestWebhook}>
                          <Icon.Bell />
                          Send
                        </Button>
                        {temporaryData.webhookResult && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => handleModalOpen(temporaryData.testEventType!)}
                          >
                            <Icon.CircleInfo />
                            Result
                          </Button>
                        )}
                      </div>
                    ),
                  },
                  {
                    id: 'description',
                    label: 'Description',
                    children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                      <Textarea
                        value={value !== undefined ? String(value) : ''}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="Description"
                        aria-invalid={isTouched && !!error}
                      />
                    ),
                  },
                  {
                    id: 'type',
                    label: 'Environment',
                    rules: { required: true },
                    children: ({ value, onChange }) =>
                      !isCreate ? (
                        <div className="flex h-10 items-center">
                          <StatusBadge>
                            {URLType === 'Production' ? <Icon.CircleCheck /> : <Icon.Sandbox />}
                            {URLType}
                          </StatusBadge>
                        </div>
                      ) : (
                        <Tabs value={String(value)} onValueChange={onChange}>
                          <TabsList>
                            <TabsTrigger value="production" disabled={!currentPlatform?.isLiveEnabled}>
                              <span className="flex items-center gap-1.5">
                                <Icon.CircleCheck />
                                Production
                              </span>
                            </TabsTrigger>
                            <TabsTrigger value="sandbox" disabled={!currentPlatform?.isLiveEnabled}>
                              <span className="flex items-center gap-1.5">
                                <Icon.Sandbox />
                                Sandbox
                              </span>
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      ),
                  },
                  {
                    id: 'enabledEvents',
                    label: 'Events',
                    children: () => (
                      <div className="flex flex-col gap-3">
                        <Tabs
                          value={allEvents ? 'all' : 'custom'}
                          onValueChange={(next) => setAllEvents(next === 'all')}
                        >
                          <TabsList>
                            <TabsTrigger value="all">All</TabsTrigger>
                            <TabsTrigger value="custom">Custom</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        {!allEvents && (
                          <Combobox
                            multiple
                            items={availableEvents}
                            value={enabledEvents.filter((eventId: string) => eventId !== '*')}
                            onValueChange={(next: string[]) => setValue('enabledEvents', next)}
                          >
                            {/* ComboboxChips can't take a ref on React 18, so a wrapper anchors the popup. */}
                            <div ref={eventsAnchorRef}>
                              <ComboboxChips>
                                <ComboboxValue>
                                  {(selected: string[]) => (
                                    <>
                                      {selected.map((eventId) => (
                                        <ComboboxChip key={eventId}>{eventId}</ComboboxChip>
                                      ))}
                                      <ComboboxChipsInput placeholder={selected.length ? '' : 'Search events'} />
                                    </>
                                  )}
                                </ComboboxValue>
                              </ComboboxChips>
                            </div>
                            <ComboboxContent anchor={eventsAnchorRef}>
                              <ComboboxEmpty>No events found.</ComboboxEmpty>
                              <ComboboxList>
                                {(eventId: string) => (
                                  <ComboboxItem key={eventId} value={eventId}>
                                    <span className="truncate">{eventId}</span>
                                    {isEventTypeDeprecated(eventId, eventTypes) && (
                                      <StatusBadge tone="warning" className="ml-auto">
                                        Deprecated
                                      </StatusBadge>
                                    )}
                                  </ComboboxItem>
                                )}
                              </ComboboxList>
                            </ComboboxContent>
                          </Combobox>
                        )}
                      </div>
                    ),
                  },
                  {
                    id: 'secret',
                    label: 'Secret',
                    hide: isCreate,
                    children: ({ value }) => <SecretInput value={typeof value === 'string' ? value : ''} />,
                  },
                  {
                    id: 'isDisabled',
                    label: 'Status',
                    hide: isCreate,
                    children: ({ value, onChange }) => (
                      <Tabs
                        value={value ? 'disabled' : 'enabled'}
                        onValueChange={(next) => onChange(next === 'disabled')}
                      >
                        <TabsList>
                          <TabsTrigger value="enabled">Enabled</TabsTrigger>
                          <TabsTrigger value="disabled">Disabled</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    ),
                  },
                ],
              },
            ],
          })}
          <div className="flex items-center justify-between px-(--page-margin) py-6">
            <Button type="button" variant="secondary" size="sm" onClick={() => navigate(-1)}>
              {id ? 'Back' : 'Cancel'}
            </Button>
            {submitButton}
          </div>
        </form>
      </FormProvider>
    </>
  );
};
